/**
 * Налаштування UI у ПРОФІЛІ юзера (рішення власника 2026-08-26).
 *
 * Раніше налаштування таблиць (порядок/приховання grid-cols:*, ширини
 * ag-widths:* / dt-widths:*) жили ЛИШЕ у localStorage — губились при зміні
 * браузера/компʼютера. Тепер сервер (`/ui-prefs`, core.user_ui_prefs) —
 * джерело істини, localStorage — дзеркало для миттєвого старту й офлайну.
 *
 * Модуль-синглтон (НЕ react-query: споживачі читають синхронно у рендері):
 *  • get(key) — з inmemory-кешу (ініціалізованого з localStorage);
 *  • set/remove — миттєво у кеш+localStorage, на сервер з debounce;
 *  • init() — тягне серверні (сервер ГОЛОВНІШИЙ за local) і повідомляє
 *    підписників → таблиці перерендерюються вже з профільними;
 *    one-shot міграція: локальні ключі, яких немає на сервері, вивантажуються.
 *  • subscribe(key, cb) — для useSyncExternalStore у компонентах.
 */
import { api } from '@/api/client';
import { currentUserId } from '@/auth/store';

const FAMILIES = ['grid-cols:', 'ag-widths:', 'dt-widths:', 'dash-widgets:',
  'dt-sort:', 'ag-sort:',   // ч.1332 — сортування колонок (DataTable / списки документів)
  'picker:',    // ч.843; ч.924 — налаштування попапів підбору
  'filters:',   // ч.951 — фільтри сторінок звітів (не губляться при переході
  'appearance:']; // ч.962 — тема (день/ніч) і масштаб шрифта
const isOurs = (k: string) => FAMILIES.some((f) => k.startsWith(f));

const cache = new Map<string, unknown>();
const listeners = new Map<string, Set<() => void>>();
let initialized = false;
let serverLoaded = false;
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

// ч.1229: чиє це дзеркало. Налаштування — у профілі КОРИСТУВАЧА, а localStorage — один на
// браузер: після іншого користувача в цьому браузері (сеанс скінчився, увійшов хтось інший)
// його дзеркало показувалось новому, а одноразове «вивантажити локальне, чого немає на
// сервері» ще й записувало чужі налаштування у профіль нового користувача.
const OWNER_KEY = 'uiPrefs.owner';

function dropLocalMirror(): void {
  cache.clear();
  try {
    const mine: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && isOurs(k)) mine.push(k);
    }
    mine.forEach((k) => localStorage.removeItem(k));
  } catch { /* приватний режим */ }
}

/** Дзеркало іншого користувача — прибрати; запамʼятати поточного власника. */
function claimMirror(): void {
  const user = currentUserId();
  if (!user) return;
  try {
    const owner = localStorage.getItem(OWNER_KEY);
    if (owner && owner !== user) dropLocalMirror();
    localStorage.setItem(OWNER_KEY, user);
  } catch { /* приватний режим */ }
}

function seedFromLocal(): void {
  claimMirror();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !isOurs(k)) continue;
      try {
        cache.set(k, JSON.parse(localStorage.getItem(k)!));
      } catch { /* битий запис */ }
    }
  } catch { /* приватний режим */ }
}
seedFromLocal();

function notify(key: string): void {
  listeners.get(key)?.forEach((cb) => cb());
}

function mirrorLocal(key: string, value: unknown | undefined): void {
  try {
    if (value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
}

function pushServer(key: string, value: unknown | undefined): void {
  // Debounce: ресайз колонок шле десятки set поспіль.
  const t = saveTimers.get(key);
  if (t) clearTimeout(t);
  saveTimers.set(key, setTimeout(() => {
    saveTimers.delete(key);
    const req = value === undefined
      // ч.892: фонова синхронізація — без системних тостів (X-Silent-Toast).
      ? api.delete(`/ui-prefs/${encodeURIComponent(key)}`, { headers: { 'X-Silent-Toast': '1' } })
      : api.put(`/ui-prefs/${encodeURIComponent(key)}`, { value }, { headers: { 'X-Silent-Toast': '1' } });
    req.catch(() => { /* офлайн/помилка — лишається у localStorage-дзеркалі */ });
  }, 600));
}

export const uiPrefs = {
  get<T>(key: string): T | null {
    return (cache.has(key) ? cache.get(key) : null) as T | null;
  },

  set(key: string, value: unknown): void {
    cache.set(key, value);
    mirrorLocal(key, value);
    pushServer(key, value);
    notify(key);
  },

  remove(key: string): void {
    cache.delete(key);
    mirrorLocal(key, undefined);
    pushServer(key, undefined);
    notify(key);
  },

  /** Усі ключі налаштувань (з памʼяті сторінки). */
  keys(): string[] {
    return [...cache.keys()];
  },

  subscribe(key: string, cb: () => void): () => void {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(cb);
    return () => listeners.get(key)?.delete(cb);
  },

  /** Виклик після логіну (AppLayout). Повторні виклики — no-op. */
  async init(): Promise<void> {
    if (initialized) return;
    initialized = true;
    claimMirror();
    try {
      const server = (await api.get<Record<string, unknown>>('/ui-prefs')).data;
      // One-shot міграція: локальне, чого немає на сервері → вивантажити.
      for (const [k, v] of cache) {
        if (isOurs(k) && !(k in server)) pushServer(k, v);
      }
      // Сервер головніший: юзер міг настроїти на іншому компʼютері.
      for (const [k, v] of Object.entries(server)) {
        if (!isOurs(k)) continue;
        cache.set(k, v);
        mirrorLocal(k, v);
        notify(k);
      }
      serverLoaded = true;
    } catch {
      initialized = false; // повторимо при наступному mount (напр. після relogin)
    }
  },

  /** Для тестів. */
  _reset(): void {
    cache.clear(); listeners.clear(); initialized = false; serverLoaded = false;
  },
  _seedFromLocal: seedFromLocal,
  get serverLoaded(): boolean { return serverLoaded; },
};

export default uiPrefs;
